import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useBOAuth } from '../../context/BOAuthContext';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { ArrowLeft, Plus, X, CreditCard, FileText, Check, Clock, AlertTriangle, Pencil, Shield } from 'lucide-react';
import { NairaInput } from '../../lib/nairaInput';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';

const formatNaira = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount || 0);
const planLabels = { full: 'Full Payment', '3_months': '3 Months', '6_months': '6 Months', '12_months': '12 Months', '24_months': '24 Months' };
const methodLabels = { cash: 'Cash', bank_transfer: 'Bank Transfer', cheque: 'Cheque', pos: 'POS', online: 'Online' };
const scheduleStatusIcon = { paid: <Check className="h-4 w-4 text-green-600" />, pending: <Clock className="h-4 w-4 text-neutral-400" />, overdue: <AlertTriangle className="h-4 w-4 text-red-500" />, upcoming: <Clock className="h-4 w-4 text-amber-500" /> };
const scheduleStatusColor = { paid: 'bg-green-50 border-green-200', pending: 'bg-white border-neutral-200', overdue: 'bg-red-50 border-red-200', upcoming: 'bg-amber-50 border-amber-200' };

const BOSaleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useBOAuth();
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPayForm, setShowPayForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [payForm, setPayForm] = useState({ payment_date: new Date().toISOString().split('T')[0], amount_paid: '', payment_method: 'bank_transfer', bank_reference: '', notes: '' });
  const [editForm, setEditForm] = useState({ total_amount: '', payment_plan: '', sale_date: '', commission: '' });
  const [submitting, setSubmitting] = useState(false);
  const [allocConfirm, setAllocConfirm] = useState({ open: false, newType: null });

  const toggleAllocation = async () => {
    const target = sale.allocation_type === 'firm' ? 'provisional' : 'firm';
    setAllocConfirm({ open: true, newType: target });
  };

  const confirmToggleAllocation = async () => {
    try {
      const plotId = sale.plot_id;
      await boAPI.setPlotAllocationType(plotId, allocConfirm.newType);
      toast.success(`Allocation set to ${allocConfirm.newType}`);
      setAllocConfirm({ open: false, newType: null });
      fetchSale();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update allocation');
    }
  };

  const fetchSale = async () => {
    try { const res = await boAPI.getSale(id); setSale(res.data); }
    catch (e) { toast.error('Failed to load sale'); navigate('/backoffice/sales'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchSale(); }, [id]);

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!payForm.amount_paid || parseFloat(payForm.amount_paid) <= 0) { toast.error('Enter a valid amount'); return; }
    setSubmitting(true);
    try {
      await boAPI.createPayment({
        client_id: sale.client_id,
        sale_id: sale.id,
        payment_date: payForm.payment_date,
        amount_paid: parseFloat(payForm.amount_paid),
        payment_method: payForm.payment_method,
        bank_reference: payForm.bank_reference || null,
        notes: payForm.notes || null,
      });
      toast.success('Payment recorded!');
      setShowPayForm(false);
      setPayForm({ payment_date: new Date().toISOString().split('T')[0], amount_paid: '', payment_method: 'bank_transfer', bank_reference: '', notes: '' });
      fetchSale();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record payment'); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {};
      if (editForm.total_amount) payload.total_amount = parseFloat(editForm.total_amount);
      if (editForm.payment_plan) payload.payment_plan = editForm.payment_plan;
      if (editForm.sale_date) payload.sale_date = editForm.sale_date;
      if (editForm.commission) payload.commission = parseFloat(editForm.commission);
      await boAPI.updateSale(id, payload);
      toast.success('Sale updated!');
      setShowEditForm(false);
      fetchSale();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update sale'); }
    finally { setSubmitting(false); }
  };

  const openEditForm = () => {
    if (sale) {
      setEditForm({
        total_amount: sale.total_amount || '',
        payment_plan: sale.payment_plan || '',
        sale_date: sale.sale_date || '',
        commission: sale.commission || '',
      });
      setShowEditForm(true);
    }
  };

  if (loading) return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" /></div></BOLayout>;
  if (!sale) return null;

  const progressPct = sale.total_amount > 0 ? Math.min(100, Math.round((sale.total_paid / sale.total_amount) * 100)) : 0;
  const isReadOnly = role === 'accountant';

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-sale-detail">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/backoffice/sales')} className="p-2 rounded-lg hover:bg-neutral-100" data-testid="back-to-sales"><ArrowLeft className="h-5 w-5" /></button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">Sale Details</h1>
              <p className="text-neutral-500">{sale.client_info?.full_name} - {sale.plot_info?.plot_number}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!isReadOnly && sale.client_id && (
              <Button onClick={() => navigate(`/backoffice/clients?edit=${sale.client_id}`)} variant="outline" className="rounded-xl" data-testid="edit-client-from-sale">
                <Pencil className="h-4 w-4 mr-2" /> Edit Client Details
              </Button>
            )}
            {!isReadOnly && (
              <Button onClick={openEditForm} variant="outline" className="rounded-xl" data-testid="edit-sale-btn">
                <Pencil className="h-4 w-4 mr-2" /> Edit Sale
              </Button>
            )}
            {!isReadOnly && sale.balance > 0 && (
              <Button onClick={() => setShowPayForm(true)} className="rounded-xl bg-green-700 hover:bg-green-800" data-testid="record-payment-btn">
                <Plus className="h-4 w-4 mr-2" /> Record Payment
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-neutral-border shadow-card"><CardContent className="p-4"><p className="text-xs text-neutral-500">Total Amount</p><p className="text-xl font-bold" data-testid="sale-total">{formatNaira(sale.total_amount)}</p></CardContent></Card>
          <Card className="border-neutral-border shadow-card"><CardContent className="p-4"><p className="text-xs text-neutral-500">Total Paid</p><p className="text-xl font-bold text-green-700" data-testid="sale-paid">{formatNaira(sale.total_paid)}</p></CardContent></Card>
          <Card className="border-neutral-border shadow-card"><CardContent className="p-4"><p className="text-xs text-neutral-500">Balance</p><p className="text-xl font-bold text-red-600" data-testid="sale-balance">{formatNaira(sale.balance)}</p></CardContent></Card>
          <Card className="border-neutral-border shadow-card"><CardContent className="p-4"><p className="text-xs text-neutral-500">Plan</p><p className="text-lg font-bold">{planLabels[sale.payment_plan]}</p></CardContent></Card>
        </div>

        {/* Progress Bar */}
        <Card className="border-neutral-border shadow-card">
          <CardContent className="p-4">
            <div className="flex justify-between text-sm text-neutral-500 mb-2"><span>Payment Progress</span><span>{progressPct}%</span></div>
            <div className="h-4 bg-neutral-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${progressPct >= 100 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${progressPct}%` }} />
            </div>
          </CardContent>
        </Card>

        {/* Discount Banner */}
        {sale.discount && sale.discount > 0 && (
          <Card className="border-emerald-300 bg-emerald-50/50 shadow-card" data-testid="discount-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Shield className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-emerald-900">Discount applied: {formatNaira(sale.discount)}</p>
                  {sale.discount_reason && (
                    <p className="text-sm text-emerald-700 mt-1">Reason: <em>{sale.discount_reason}</em></p>
                  )}
                  <p className="text-xs text-emerald-700 mt-1">This discount is already reflected in the Total Amount above.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Late Penalty Banner */}
        {sale.penalty && sale.penalty.penalty_amount > 0 && (
          <Card className="border-red-300 bg-red-50/50 shadow-card" data-testid="penalty-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-red-900">
                    {sale.penalty.status === 'grace_extension' ? 'Grace extension penalty (+20%)' : `Overdue penalty (+${sale.penalty.penalty_pct}%)`}
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    This sale is <strong>{sale.penalty.months_past_sale} months</strong> past the sale date.{' '}
                    A penalty of <strong>{formatNaira(sale.penalty.penalty_amount)}</strong> ({sale.penalty.penalty_pct}% of the outstanding balance) applies.
                  </p>
                  <p className="text-sm text-red-800 mt-2">
                    Effective balance to collect: <strong>{formatNaira(sale.effective_balance)}</strong>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Allocation Status */}
        {sale.allocation_type && sale.payment_status !== 'cancelled' && (
          <Card className={`shadow-card ${sale.allocation_type === 'firm' ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${sale.allocation_type === 'firm' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {sale.allocation_type === 'firm' ? <Shield className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
                  </div>
                  <div>
                    <p className="font-semibold text-neutral-900" data-testid="allocation-status-label">
                      {sale.allocation_type === 'firm' ? 'Firm Allocation' : 'Provisional Allocation'}
                    </p>
                    <p className="text-sm text-neutral-600">
                      {sale.allocation_type === 'firm'
                        ? 'Fully paid — plot is permanently assigned to this client.'
                        : 'Client is on installments — plot can be reassigned if needed.'}
                    </p>
                  </div>
                </div>
                {role === 'owner' && (
                  <Button
                    onClick={toggleAllocation}
                    variant="outline"
                    size="sm"
                    className="rounded-xl whitespace-nowrap"
                    data-testid="toggle-allocation-btn"
                  >
                    {sale.allocation_type === 'firm' ? 'Mark as Provisional' : 'Mark as Firm'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Sale Form */}
        {showEditForm && (
          <Card className="border-amber-200 shadow-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Edit Sale</CardTitle><CardDescription>Update sale details</CardDescription></div>
              <button onClick={() => setShowEditForm(false)} className="p-2 hover:bg-neutral-100 rounded-lg"><X className="h-5 w-5" /></button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEdit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label>Total Amount (Naira)</Label>
                  <NairaInput value={editForm.total_amount} onChange={(v) => setEditForm({ ...editForm, total_amount: v })} className="mt-1.5 rounded-xl" data-testid="edit-sale-amount" />
                </div>
                <div>
                  <Label>Payment Plan</Label>
                  <select value={editForm.payment_plan} onChange={(e) => setEditForm({ ...editForm, payment_plan: e.target.value })} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="edit-sale-plan">
                    <option value="full">Full Payment</option>
                    <option value="3_months">3 Months</option>
                    <option value="6_months">6 Months</option>
                    <option value="12_months">12 Months</option>
                    <option value="24_months">24 Months</option>
                  </select>
                </div>
                <div>
                  <Label>Sale Date</Label>
                  <Input type="date" value={editForm.sale_date} onChange={(e) => setEditForm({ ...editForm, sale_date: e.target.value })} className="mt-1.5 rounded-xl" data-testid="edit-sale-date" />
                </div>
                <div>
                  <Label>Commission (Naira)</Label>
                  <NairaInput value={editForm.commission} onChange={(v) => setEditForm({ ...editForm, commission: v })} className="mt-1.5 rounded-xl" />
                </div>
                <div className="sm:col-span-2 lg:col-span-4 flex gap-3 justify-end pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowEditForm(false)} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="rounded-xl bg-amber-600 hover:bg-amber-700 text-white" data-testid="edit-sale-submit">{submitting ? 'Saving...' : 'Update Sale'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Payment Form */}
        {showPayForm && (
          <Card className="border-green-200 shadow-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>Record Payment</CardTitle><CardDescription>Balance: {formatNaira(sale.balance)}</CardDescription></div>
              <button onClick={() => setShowPayForm(false)} className="p-2 hover:bg-neutral-100 rounded-lg"><X className="h-5 w-5" /></button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePayment} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div><Label>Amount (Naira) *</Label><NairaInput value={payForm.amount_paid} onChange={(v) => setPayForm({ ...payForm, amount_paid: v })} className="mt-1.5 rounded-xl" data-testid="pay-form-amount" /></div>
                <div><Label>Payment Date *</Label><Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} className="mt-1.5 rounded-xl" /></div>
                <div>
                  <Label>Payment Method *</Label>
                  <select value={payForm.payment_method} onChange={(e) => setPayForm({ ...payForm, payment_method: e.target.value })} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="pay-form-method">
                    <option value="cash">Cash</option><option value="bank_transfer">Bank Transfer</option><option value="cheque">Cheque</option><option value="pos">POS</option><option value="online">Online</option>
                  </select>
                </div>
                <div><Label>Bank Reference</Label><Input value={payForm.bank_reference} onChange={(e) => setPayForm({ ...payForm, bank_reference: e.target.value })} className="mt-1.5 rounded-xl" /></div>
                <div className="sm:col-span-2"><Label>Notes</Label><Input value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} className="mt-1.5 rounded-xl" /></div>
                <div className="sm:col-span-2 lg:col-span-3 flex gap-3 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowPayForm(false)} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="rounded-xl bg-green-700 hover:bg-green-800" data-testid="pay-form-submit">{submitting ? 'Recording...' : 'Record Payment'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Payment Schedule */}
          <Card className="border-neutral-border shadow-card">
            <CardHeader><CardTitle className="text-lg">Payment Schedule</CardTitle></CardHeader>
            <CardContent>
              {sale.payment_schedule && sale.payment_schedule.length > 0 ? (
                <div className="space-y-2">
                  {sale.payment_schedule.map((s) => (
                    <div key={s.installment_number} className={`flex items-center justify-between p-3 rounded-xl border ${scheduleStatusColor[s.status] || 'bg-white border-neutral-200'}`}>
                      <div className="flex items-center gap-3">
                        {scheduleStatusIcon[s.status] || <Clock className="h-4 w-4 text-neutral-400" />}
                        <div>
                          <p className="font-medium text-sm">Installment {s.installment_number}</p>
                          <p className="text-xs text-neutral-500">Due: {s.due_date}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-sm">{formatNaira(s.amount_due)}</p>
                        <p className="text-xs text-neutral-500 capitalize">{s.status}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-neutral-500 text-sm">{sale.payment_plan === 'full' ? 'Full payment - no installments' : 'No schedule generated'}</p>
              )}
            </CardContent>
          </Card>

          {/* Payment History */}
          <Card className="border-neutral-border shadow-card">
            <CardHeader><CardTitle className="text-lg">Payment History</CardTitle></CardHeader>
            <CardContent>
              {sale.payments && sale.payments.length > 0 ? (
                <div className="space-y-2">
                  {sale.payments.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-3 rounded-xl border border-neutral-200 hover:bg-neutral-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <CreditCard className="h-4 w-4 text-green-600" />
                        <div>
                          <p className="font-medium text-sm">{formatNaira(p.amount_paid)}</p>
                          <p className="text-xs text-neutral-500">{p.payment_date} - {methodLabels[p.payment_method] || p.payment_method}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <button onClick={() => navigate(`/backoffice/receipt/${p.id}`)} className="text-xs text-primary-700 hover:underline flex items-center gap-1" data-testid={`view-receipt-${p.id}`}>
                          <FileText className="h-3 w-3" /> Receipt
                        </button>
                        <p className="text-xs text-neutral-400 mt-0.5">{p.receipt_number}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-neutral-500 text-sm">No payments recorded yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={allocConfirm.open}
        onOpenChange={(o) => !o && setAllocConfirm({ open: false, newType: null })}
        title={allocConfirm.newType === 'firm' ? 'Mark as Firm Allocation?' : 'Mark as Provisional?'}
        description={allocConfirm.newType === 'firm'
          ? 'This locks the plot as permanently assigned to this client. Use this when you trust the client even though their balance is not zero (e.g., pending bank transfer).'
          : 'This makes the plot revocable — you will be able to cancel the sale and reassign the plot to someone else.'}
        confirmLabel={`Yes, mark as ${allocConfirm.newType}`}
        destructive={false}
        onConfirm={confirmToggleAllocation}
      />
    </BOLayout>
  );
};

export default BOSaleDetail;
